import { Plugin } from 'prosemirror-state'
import { targetCommentMark } from './utils'
import { CommentMarkDataset, InitConfig, State } from './Types'
import { CommentsView } from './CommentsView'
import { decorationsV2 } from './decorations'
import { responsiveDesignLogic } from 'hooks/useResponsiveDesign'
import { CommentStatus } from 'types'
import { COMMENTS_PLUGIN_KEY, getCommentsPluginState } from './Constants'
import { COMMENT_VIEW } from 'components/DocRightRail/Constants'
import { DecorationSet } from 'prosemirror-view'
import { CollaborationDrawer } from 'components/CollaborationDrawer/CollaborationDrawer'

export const CommentsPlugin = (config: InitConfig) => {
  return new Plugin({
    key: COMMENTS_PLUGIN_KEY,
    state: {
      init() {
        return {
          docStore: config.docStore,
          queryClient: config.queryClient,
          enabled: config.enabled,
          commentsStore: config.commentsStore,
        }
      },
      apply(tr, state) {
        const meta = tr.getMeta(COMMENTS_PLUGIN_KEY)
        if (!meta) return state

        return { ...state, enabled: meta.enabled }
      },
    },
    appendTransaction: (_tx, _oldState, newState) => {
      const { selection, tr } = newState
      if (selection.empty && newState.storedMarks && newState.schema.marks.comment.isInSet(newState.storedMarks)) {
        return tr.removeStoredMark(newState.schema.marks.comment)
      }
    },
    view(view) {
      return new CommentsView(view)
    },
    props: {
      decorations(state) {
        const { commentsStore }: State = getCommentsPluginState(state)
        if (!commentsStore) {
          return DecorationSet.empty
        }
        return decorationsV2(state)
      },
      handleDOMEvents: {
        touchstart: (view, event) => {
          const { isMobile } = responsiveDesignLogic()
          const el = targetCommentMark(event)
          if (!el) {
            return false
          }
          const { uuid } = el.dataset as CommentMarkDataset
          const { docStore, commentsStore }: State = getCommentsPluginState(view.state)
          const mobxComment = docStore?.rootStore.comments.getByUuid(uuid)
          const isCommentResolved = mobxComment?.status === CommentStatus.Resolved
          const commentId = mobxComment?.id
          if (isMobile) {
            // We don't want to click to open on resolved
            if (!isCommentResolved) {
              // This was causing problems with the placement of the cursor in mobile
              // That is why the timeout
              setTimeout(() => {
                docStore?.rootStore.comments.select(commentId)
                commentsStore?.select(uuid)
                docStore?.collabItemsStore.selectById(commentId)
                docStore?.rootStore.drawersStore.openDrawer(CollaborationDrawer)
              })
            }
            return false
          }
          docStore?.rootStore.comments.select(commentId)
          commentsStore?.select(uuid)
          docStore?.rightRailStore.setActiveTab(COMMENT_VIEW)
          docStore?.rightRailStore.openSection('comments')

          return true
        },
        click: (view, event) => {
          if ('touchstart' in window) {
            return false
          }
          const { isMobile } = responsiveDesignLogic()
          const el = targetCommentMark(event)
          if (!el) {
            return false
          }
          const { uuid } = el.dataset as CommentMarkDataset
          const { docStore, commentsStore }: State = getCommentsPluginState(view.state)
          const mobxComment = docStore?.rootStore.comments.getByUuid(uuid)
          const isCommentResolved = mobxComment?.status === CommentStatus.Resolved
          const commentId = mobxComment?.id
          if (isMobile) {
            // We don't want to click to open on resolved
            if (!isCommentResolved) {
              // This was causing problems with the placement of the cursor in mobile
              // That is why the timeout
              setTimeout(() => {
                docStore?.rootStore.comments.select(commentId)
                docStore?.collabItemsStore.selectById(commentId)
                commentsStore?.select(uuid)
              })
            }
            return false
          }
          docStore?.rootStore.comments.select(commentId)
          commentsStore?.select(uuid)
          docStore?.rightRailStore.setActiveTab(COMMENT_VIEW)
          docStore?.rightRailStore.openSection('comments')

          return true
        },
        mouseout: (view, event) => {
          const el = targetCommentMark(event)
          if (!el) {
            return false
          }

          const { docStore, commentsStore }: State = getCommentsPluginState(view.state)
          docStore?.rootStore.comments.highlight(undefined)
          commentsStore?.highlight(undefined)

          return false
        },
        mouseover: (view, event) => {
          const el = targetCommentMark(event)
          if (!el) {
            return false
          }

          const { uuid } = el.dataset as CommentMarkDataset
          const { docStore, commentsStore }: State = getCommentsPluginState(view.state)
          docStore?.rootStore.comments.highlight(docStore.rootStore.comments.getByUuid(uuid)?.id)
          commentsStore?.highlight(uuid)

          return false
        },
      },
    },
  })
}
